import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import { isInternalAccount } from "@onmo/auth";
import { FeaturedCard, Query } from "@onmo/graphql/graphql";
import { featuredCardsGQL } from "../graphql";

const pageSize = 3;

export const useFeaturedCards = () => {
  const isFetchingMoreRef = useRef(false);
  const {
    data: featuredCardsData,
    fetchMore,
    loading,
  } = useQuery<{
    featuredCards: Query["featuredCards"];
  }>(featuredCardsGQL, {
    variables: {
      input: { group: "main", page: { limit: pageSize, offset: 0 } },
    },
  });
  const homeFeed = featuredCardsData?.featuredCards?.items;
  const total = featuredCardsData?.featuredCards?.total ?? 0;
  const hasMore = (homeFeed?.length ?? 0) < total;
  const canSeeRestricted = isInternalAccount();

  // Restricted cards belong to partners that are not live yet, so only QA/Admin accounts see them
  const isVisible = useCallback(
    (card: FeaturedCard) =>
      canSeeRestricted || String(card.metadata?.restricted) !== "true",
    [canSeeRestricted],
  );
  const visibleFeed = useMemo(
    () => homeFeed?.filter(isVisible),
    [homeFeed, isVisible],
  );

  const loadMore = useCallback(async () => {
    let offset = homeFeed?.length ?? 0;
    if (isFetchingMoreRef.current || loading || offset >= total) return;

    isFetchingMoreRef.current = true;
    try {
      // A page of only restricted cards adds nothing to scroll to, so fetch the next one
      while (offset < total) {
        const { data } = await fetchMore({
          variables: {
            input: { group: "main", page: { limit: pageSize, offset } },
          },
        });
        const page = data?.featuredCards?.items ?? [];
        if (!page.length) break;
        offset += page.length;
        if (page.some(isVisible)) break;
      }
    } finally {
      isFetchingMoreRef.current = false;
    }
  }, [fetchMore, homeFeed?.length, isVisible, loading, total]);

  // The feed's load-more trigger sits on the second to last card, so it never renders below
  // two
  useEffect(() => {
    if (hasMore && (visibleFeed?.length ?? 0) < 2) loadMore();
  }, [hasMore, visibleFeed?.length, loadMore]);

  return {
    homeFeed: visibleFeed,
    hasMore,
    loadMore,
  };
};
